import { formatExpectedMetric, getExpectedAssists, getExpectedGoals } from "../../eventMetrics";
import {
	aggregateExpectedGoals,
	isEventInSelectedPeriods,
	isPenaltyDetectionReliable,
} from "../../expectedGoalsEvents";
import type { GameTeamStatResponse, GameTeamStatTypeEnum, GPEventResponse } from "../../types";
import { TeamStatType } from "./definition";

const STORED_TEAM_STAT_CATEGORY = "offensive";
const UNAVAILABLE_VALUE_LABEL = "-";

const STORED_STAT_TYPE_BY_TEAM_STAT_TYPE: Partial<Record<TeamStatType, GameTeamStatTypeEnum>> = {
	[TeamStatType.TotalXg]: "totalXg",
	[TeamStatType.TotalXa]: "totalXa",
	[TeamStatType.NonPenaltyXg]: "nonPenaltyXg",
	[TeamStatType.XgPerShot]: "nonPenaltyXgPerShot",
};

const storedExpectedGoalsStatTypes = new Set<GameTeamStatTypeEnum>(
	Object.values(STORED_STAT_TYPE_BY_TEAM_STAT_TYPE).filter(
		(statType): statType is GameTeamStatTypeEnum => statType !== undefined,
	),
);

export function hasStoredTeamExpectedGoals(gameTeamStat?: GameTeamStatResponse | null): boolean {
	if (gameTeamStat == null) {
		return false;
	}

	return gameTeamStat.stats.some(
		(stat) =>
			stat.category === STORED_TEAM_STAT_CATEGORY &&
			storedExpectedGoalsStatTypes.has(stat.statType) &&
			(stat.homeTeamValue !== null || stat.awayTeamValue !== null),
	);
}

export interface TeamExpectedGoalsValues {
	home: number | null;
	away: number | null;
}

export interface TeamExpectedGoalsContext {
	gameTeamStat?: GameTeamStatResponse | null;
	isFullMatchSelection: boolean;
}

export function formatTeamExpectedGoalsValue(metric: number | null): string {
	if (metric === null) {
		return UNAVAILABLE_VALUE_LABEL;
	}

	return formatExpectedMetric(metric);
}

function unavailableValues(): TeamExpectedGoalsValues {
	return { home: null, away: null };
}

function getStoredTeamExpectedGoals(
	statType: TeamStatType,
	gameTeamStat: GameTeamStatResponse,
): TeamExpectedGoalsValues {
	const storedStatType = STORED_STAT_TYPE_BY_TEAM_STAT_TYPE[statType];

	if (storedStatType === undefined) {
		return unavailableValues();
	}

	const storedStat = gameTeamStat.stats.find(
		(stat) => stat.category === STORED_TEAM_STAT_CATEGORY && stat.statType === storedStatType,
	);

	if (storedStat === undefined) {
		return unavailableValues();
	}

	return { home: storedStat.homeTeamValue, away: storedStat.awayTeamValue };
}

function expectedGoalsPerShot(nonPenaltyExpectedGoals: number, nonPenaltyShots: number): number | null {
	if (nonPenaltyShots === 0) {
		return null;
	}

	return nonPenaltyExpectedGoals / nonPenaltyShots;
}

function hasEventExpectedMetric(
	statType: TeamStatType,
	periods: string[],
	events: GPEventResponse[],
): boolean {
	return events.some((event) => {
		if (isEventInSelectedPeriods(event, periods) === false) {
			return false;
		}

		if (statType === TeamStatType.TotalXa) {
			return event.type === "pass" && getExpectedAssists(event.details) !== null;
		}

		return event.type === "shot" && getExpectedGoals(event.details) !== null;
	});
}

function getPeriodFilteredTeamExpectedGoals(
	statType: TeamStatType,
	homeGameTeamId: number,
	awayGameTeamId: number,
	periods: string[],
	events: GPEventResponse[],
): TeamExpectedGoalsValues {
	const home = aggregateExpectedGoals(events, periods, (event) => event.gameTeamId === homeGameTeamId);
	const away = aggregateExpectedGoals(events, periods, (event) => event.gameTeamId === awayGameTeamId);
	const canExcludePenalties = isPenaltyDetectionReliable(events, periods);

	switch (statType) {
		case TeamStatType.TotalXg:
			return { home: home.expectedGoals, away: away.expectedGoals };
		case TeamStatType.TotalXa:
			return { home: home.expectedAssists, away: away.expectedAssists };
		case TeamStatType.NonPenaltyXg:
			if (canExcludePenalties === false) {
				return unavailableValues();
			}

			return { home: home.nonPenaltyExpectedGoals, away: away.nonPenaltyExpectedGoals };
		case TeamStatType.XgPerShot:
			if (canExcludePenalties === false) {
				return unavailableValues();
			}

			return {
				home: expectedGoalsPerShot(home.nonPenaltyExpectedGoals, home.nonPenaltyShotsWithExpectedGoals),
				away: expectedGoalsPerShot(away.nonPenaltyExpectedGoals, away.nonPenaltyShotsWithExpectedGoals),
			};
		default:
			return unavailableValues();
	}
}

export function resolveTeamExpectedGoals(
	statType: TeamStatType,
	homeGameTeamId: number,
	awayGameTeamId: number,
	periods: string[],
	events: GPEventResponse[],
	context?: TeamExpectedGoalsContext,
): TeamExpectedGoalsValues {
	if (context?.isFullMatchSelection === true) {
		const gameTeamStat = context.gameTeamStat;

		const storedValues = gameTeamStat == null
			? unavailableValues()
			: getStoredTeamExpectedGoals(statType, gameTeamStat);

		if (
			(storedValues.home !== null && storedValues.away !== null)
			|| hasEventExpectedMetric(statType, periods, events) === false
		) {
			return storedValues;
		}

		const eventValues = getPeriodFilteredTeamExpectedGoals(
			statType,
			homeGameTeamId,
			awayGameTeamId,
			periods,
			events,
		);

		return {
			home: storedValues.home ?? eventValues.home,
			away: storedValues.away ?? eventValues.away,
		};
	}

	return getPeriodFilteredTeamExpectedGoals(statType, homeGameTeamId, awayGameTeamId, periods, events);
}
